import { useEffect, useState } from 'react';
import { useSurvey } from '@/features/questionnaire/hooks/useSurvey';
import { useNavigate } from 'react-router-dom';
import { useSubmitSurvey } from '@/features/questionnaire/hooks/useSubmitSurvey';
import { useRiskAssessment } from '@/features/questionnaire/hooks/useRiskAssessment';
import { SurveyLoader } from '@/features/questionnaire/ui/SurveyLoader/SurveyLoader';

export const QuestionnairePage = () => {
    const { survey } = useSurvey();
    const navigate = useNavigate();
    const [finished, setFinished] = useState(false);

    const [currentBlockIndex, setCurrentBlockIndex] = useState<number>(
        Number(localStorage.getItem('currentBlockIndex')) || 0
    );
    const [answers, setAnswers] = useState<Record<number, any>>(
        JSON.parse(localStorage.getItem('answers') || '{}')
    );
    const [touched, setTouched] = useState<Record<number, boolean>>({});
    const [errors, setErrors] = useState<Record<number, boolean>>({});

    const { submitSurvey, isSubmitting, progress: submitProgress } = useSubmitSurvey();
    const { requestRiskAssessment, loading: isAssessing, progress: assessProgress } = useRiskAssessment();

    const combinedProgress = isSubmitting ? submitProgress : isAssessing ? assessProgress : 0;

    useEffect(() => {
        const sendAll = async () => {
            if (!survey) return;
            const instanceId = await submitSurvey(answers, survey);


            if (instanceId) {
                requestRiskAssessment(instanceId);
            }
        };

        if (finished && survey) {
            sendAll();
        }
    }, [finished]);

    useEffect(() => {
        if (combinedProgress === 100) {
            navigate('/checkup');
        }
    }, [combinedProgress]);

    useEffect(() => {
        localStorage.setItem('currentBlockIndex', String(currentBlockIndex));
        localStorage.setItem('answers', JSON.stringify(answers));
    }, [currentBlockIndex, answers]);

    if (!survey || !survey.blocks || !survey.blocks.length) return <div>Загрузка...</div>;

    const currentBlock = survey.blocks[currentBlockIndex];
    const questions = Array.isArray(currentBlock?.questions) ? currentBlock.questions : [];

    const allPrevQuestionsCount = survey.blocks
        .slice(0, currentBlockIndex)
        .reduce((acc: number, block: { questions?: any[] }) => acc + (block.questions?.length || 0), 0);

    const handleChange = (questionId: number, value: any) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
        setTouched((prev) => ({ ...prev, [questionId]: true }));
    };

    const validateBlock = () => {
        const blockErrors: Record<number, boolean> = {};
        questions.forEach((q: any) => {
            const val = answers[q.question_id];
            if (q.required && (val === undefined || val === '' || (Array.isArray(val) && val.length === 0))) {
                blockErrors[q.question_id] = true;
            }
        });
        setErrors(blockErrors);
        return Object.keys(blockErrors).length === 0;
    };

    const handleNextBlock = () => {
        if (!validateBlock()) return;
        if (currentBlockIndex < survey.blocks.length - 1) {
            setCurrentBlockIndex(currentBlockIndex + 1);
        } else {
            localStorage.removeItem('currentBlockIndex');
            localStorage.removeItem('answers');
            setFinished(true);
        }
    };

    const handlePrevBlock = () => {
        if (currentBlockIndex > 0) {
            setCurrentBlockIndex(currentBlockIndex - 1);
        }
    };

    const renderInput = (question: any) => {
        const type = question.question_type;
        const value = answers[question.question_id] || '';
        const hasError = errors[question.question_id];

        if (type === 'number') {
            return (
                <div className={`test-question-variants__item ${hasError ? 'has-error' : ''}`}>
                    <fieldset className="form__fields">
                        <div className="form__row">
                            <div className="input">
                                <label className="text-input">
                                    <input
                                        className="text-input__input"
                                        type="number"
                                        value={value}
                                        onChange={(e) => handleChange(question.question_id, e.target.value)}
                                    />
                                </label>
                            </div>
                        </div>
                    </fieldset>
                </div>
            );
        }

        if (type === 'radio' || type === 'sex') {
            return (
                <div className={`test-question-variants__inner ${type === 'sex' ? 'test-question-variants_horizontal' : ''}`}>
                    {question.options?.map((opt: any) => (
                        <label className="test-question-variant test-question-variants__item" key={opt.option_id}>
                            {type === 'sex' && (
                                <div className="test-question-variant__image-wrapper">
                                    <img
                                        className="test-question-variant__image test-question-variant__image_auto"
                                        src={opt.value === 'м' ? '/img/test-question-gender-male-image.svg' : '/img/test-question-gender-female-image.svg'}
                                        alt=""
                                    />
                                </div>
                            )}
                            <div className="test-question-variant__field">
                                <div className="checkbox-input">
                                    <input
                                        className="checkbox-input__input"
                                        type="radio"
                                        name={`q${question.question_id}`}
                                        value={opt.value}
                                        checked={value === opt.value}
                                        onChange={() => handleChange(question.question_id, opt.value)}
                                    />
                                    <span className="checkbox-input__label">{opt.option_text}</span>
                                </div>
                            </div>
                        </label>
                    ))}
                </div>
            );
        }

        if (type === 'checkbox') {
            const current = Array.isArray(value) ? value : [];
            return (
                <div className="test-question-variants__inner">
                    {question.options?.map((opt: any) => (
                        <label className="test-question-variant test-question-variants__item" key={opt.option_id}>
                            <div className="test-question-variant__field">
                                <div className="checkbox-input">
                                    <input
                                        className="checkbox-input__input"
                                        type="checkbox"
                                        value={opt.value}
                                        checked={current.includes(opt.value)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                handleChange(question.question_id, [...current, opt.value]);
                                            } else {
                                                handleChange(question.question_id, current.filter((v: any) => v !== opt.value));
                                            }
                                        }}
                                    />
                                    <span className="checkbox-input__label">{opt.option_text}</span>
                                </div>
                            </div>
                        </label>
                    ))}
                </div>
            );
        }

        return <div>Неизвестный тип вопроса</div>;
    };

    const progressTop = Math.round(((currentBlockIndex + 1) / survey.blocks.length) * 100);
    if (isSubmitting || isAssessing) return <SurveyLoader progress={combinedProgress} />;

    return (
        <main className="layout__content quest">
            <section className="section layout__section test response-testing-form">
                <div className="progress-bar" style={{ width: `${progressTop}%`, height: '4px', background: '#00a79d' }} />
                <div className="container">
                    <div className="section__content">
                        <form className="test__question test-question bionmax_user_testing_form">
                            <div className="testCount">
                                <b>Блок:</b> <span style={{ color: '#277871' }}>{currentBlock.title} <span style={{ marginLeft: '8px' }}>{currentBlockIndex + 1} </span>/ <span style={{ color: '#7E7E7E' }}>{survey.blocks.length}</span></span>
                            </div>
                            {questions.map((question: any, index: number) => (
                                <div className="tab showed" key={index} style={{ display: 'block' }}>
                                    <div className="test-question__header">
                                        <h5 className="title test-question__title">
                                            <span className="number_form">{allPrevQuestionsCount + index + 1}.</span> {question.question_text}
                                        </h5>
                                        {question.description && (
                                            <p className="test-question__description">{question.description}</p>
                                        )}
                                    </div>
                                    <div className="test-question__content">
                                        <div className="test-question__variants test-question-variants">
                                            {renderInput(question)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button style={{ margin: '0 auto', display: 'flex', marginTop: '-80px'}} type="button" className="button button_outline" onClick={handleNextBlock}>
                                <span className="button__text">Ок ✓</span>
                            </button>
                            <div className="test__bottom" style={{ alignItems: 'center', justifyContent: 'space-between', marginTop: '40px' }}>
                                <div className="test__bottom-count" style={{ fontWeight: 800, fontSize: '22px' }}>
                                    <span style={{ color: '#277871' }}><span style={{ marginLeft: '8px' }}>{currentBlockIndex + 1} </span>/ <span style={{ color: '#7E7E7E' }}>{survey.blocks.length}</span></span>
                                </div>
                                <div className="" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <button
                                        type="button"
                                        className="button button_outline"
                                        onClick={handlePrevBlock}
                                        style={{ borderStyle: 'dashed', opacity: currentBlockIndex === 0 ? 0.5 : 1 }}
                                    >
                                        <span className="button__text">Предыдущий блок</span>
                                    </button>

                                    <button
                                        type="button"
                                        className="button button_outline"
                                        onClick={handleNextBlock}
                                        style={{ borderStyle: 'dashed' }}
                                    >
                                        <span className="button__text">Следующий блок</span>
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </main>
    );
};
